import {
	ConnectionInterface,
	type DataID,
	DefaultHandlerProvider,
	type EnvironmentConfig,
	type LogFunction,
	type MutableRecordSource,
	type OperationLoader,
	RecordSource,
	RelayFeatureFlags,
	type RelayFieldLogger,
} from 'relay-runtime';
import LiveResolverStore, {
	type ActorIdentifier,
	type GetDataID,
	// @ts-ignore
} from 'relay-runtime/lib/store/experimental-live-resolvers/LiveResolverStore';
import {
	type HandleFieldPayload,
	type Handler,
	type RecordProxy,
	type RecordSourceProxy,
	type Scheduler,
} from 'relay-runtime/lib/store/RelayStoreTypes';
import invariant from 'tiny-invariant';
import warning from 'tiny-warning';

import { fg } from '@atlaskit/platform-feature-flags';
import RelayDataID from '@atlassian/relay-data-id';
import type { ExternalDependencies } from '@atlassian/relay-fetch/multi-fetch';
import RelayMissingFieldHandlers from '@atlassian/relay-missing-field-handlers';
import { createNetwork, network as defaultNetwork } from '@atlassian/relay-network';
import RelayScheduler from '@atlassian/relay-scheduler';

type Props = {
	log?: EnvironmentConfig['log'];
	requiredFieldLogger?: RelayFieldLogger;
	network?: EnvironmentConfig['network'];
	getDataID?: EnvironmentConfig['getDataID'];
	recordSource?: RecordSource;
	networkOptions?: ExternalDependencies;
};

const createStore = (
	source: MutableRecordSource,
	options?: {
		actorIdentifier?: ActorIdentifier;
		gcReleaseBufferSize?: number;
		gcScheduler?: Scheduler;
		getDataID?: GetDataID;
		log?: LogFunction;
		operationLoader?: OperationLoader;
		queryCacheExpirationTime?: number;
		shouldProcessClientComponents?: boolean;
		treatMissingFieldsAsNull?: boolean;
	},
) => {
	RelayFeatureFlags.ENABLE_RELAY_RESOLVERS = true;
	return new LiveResolverStore(source, options);
};

export function getDefaultRelayEnvironmentConfig({
	log,
	requiredFieldLogger,
	network,
	networkOptions,
	getDataID,
	recordSource,
}: Props): EnvironmentConfig {
	return {
		configName: 'Atlassian Graphql Gateway',
		network: network || getEnvironmentNetwork(networkOptions),
		store: createStore(recordSource || new RecordSource(), {
			log: log ?? undefined,
			getDataID: getDataID ?? RelayDataID,
		}),
		log,
		relayFieldLogger: requiredFieldLogger,
		getDataID: getDataID ?? RelayDataID,
		scheduler: RelayScheduler,
		missingFieldHandlers: RelayMissingFieldHandlers,
	};
}

export function getSharedRelayEnvironmentConfig({
	log,
	requiredFieldLogger,
	getDataID,
	networkOptions,
}: Omit<Props, 'network'>): EnvironmentConfig;

export function getSharedRelayEnvironmentConfig({
	log,
	requiredFieldLogger,
	getDataID,
}: Omit<Props, 'network' | 'networkOptions'>): EnvironmentConfig;

export function getSharedRelayEnvironmentConfig({
	log,
	requiredFieldLogger,
	networkOptions,
	getDataID,
	recordSource,
}: Props): EnvironmentConfig {
	return {
		configName: 'Atlassian Shared Graphql Gateway',
		network: getEnvironmentNetwork(networkOptions),
		store: createStore(recordSource || new RecordSource(), {
			log: log ?? undefined,
			getDataID: getDataID ?? RelayDataID,
		}),
		log,
		relayFieldLogger: requiredFieldLogger,
		getDataID: getDataID ?? RelayDataID,
		scheduler: RelayScheduler,
		missingFieldHandlers: RelayMissingFieldHandlers,
		handlerProvider: fg('platform_relay_config_deleteedge_handler') ? HandlerProvider : undefined,
	};
}

function HandlerProvider(handle: string): Handler {
	switch (handle) {
		case 'deleteEdge':
			return DeleteEdgeHandler;
		default:
			return DefaultHandlerProvider(handle);
	}
}

const getEnvironmentNetwork = (environmentOptions: Props['networkOptions']) => {
	if (environmentOptions) {
		return createNetwork(environmentOptions);
	}

	return defaultNetwork;
};

// Copied from ConnectionHandler.deleteNode implementation with a single change in behaviour:
// nodeID is compared to the `id` field of the node, rather than node.getDataID().
// This is a sane thing to do within the scope of a single connection even in the light of
// id collisions present in AGG for the entities of different types.
// Such change makes @deleteEdge directive usable again.
function deleteNode(record: RecordProxy, nodeID: DataID): void {
	const { EDGES, NODE } = ConnectionInterface.get();

	const edges = record.getLinkedRecords(EDGES);
	if (!edges) {
		return;
	}
	let nextEdges;
	for (let ii = 0; ii < edges.length; ii++) {
		const edge = edges[ii];
		const node = edge && edge.getLinkedRecord(NODE);
		// N.B. node.getValue('id') instead of node.getDataID()
		if (node != null && node.getValue('id') === nodeID) {
			if (nextEdges === undefined) {
				nextEdges = edges.slice(0, ii);
			}
		} else if (nextEdges !== undefined) {
			nextEdges.push(edge);
		}
	}
	if (nextEdges !== undefined) {
		record.setLinkedRecords(nextEdges, EDGES);
	}
}

// Copied from DeleteEdgeHandler implementation in Relay with a single change in behaviour:
// it calls deleteNode above instead of ConnectionHandler.deleteNode.
const DeleteEdgeHandler = {
	update: (store: RecordSourceProxy, payload: HandleFieldPayload) => {
		const record = store.get(payload.dataID);
		if (record == null) {
			return;
		}
		// @types/relay-runtime is broken, handleArgs is present in the original Flow types.
		const { connections } = (
			payload as HandleFieldPayload & { handleArgs: { connections: string[] } }
		).handleArgs;
		invariant(connections != null, 'MutationHandlers: Expected connection IDs to be specified.');
		const idOrIds = record.getValue(payload.fieldKey);
		const idList = Array.isArray(idOrIds) ? idOrIds : [idOrIds];
		idList.forEach((id) => {
			if (typeof id === 'string') {
				for (const connectionID of connections) {
					const connection = store.get(connectionID);
					if (connection == null) {
						warning(false, `[Relay] The connection with id ${connectionID} doesn't exist.`);
						continue;
					}
					deleteNode(connection, id);
				}
			}
		});
	},
};
